import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useAccessControl, useMediaSoup, usePushToTalk, useSocket, useRecordings } from "./hooks";
import { VideoPlayer, HlsVideoPlayer, AudioPlayer, MetadataPanel, RecordingsList, RecordingPlayer, SettingsPanel } from "./components";
import { GPSMap, GPSMapStyles } from "./components/GPSMap";
import { getHlsUrl, generateViewerId, DEFAULT_VOLUME, STALE_VIDEO_THRESHOLD, STALE_CHECK_INTERVAL, UNIT_STALE_TIMEOUT, SERVER_URL } from "./utils/constants";

export default function MediaSoupViewer() {
  // ============= STATE =============
  const [activeUnits, setActiveUnits] = useState(new Map());
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [activeTab, setActiveTab] = useState('live'); // 'live' | 'recordings' | 'settings'
  const [connectionStatus, setConnectionStatus] = useState("Awaiting Access");
  const [currentTime, setCurrentTime] = useState(new Date());
  const [networkQuality, setNetworkQuality] = useState('good');
  const [dashboardStats, setDashboardStats] = useState({ todayClips: 0, allClips: 0, diskFreeGB: null });
  const [playingRecording, setPlayingRecording] = useState(null);

  const viewerId = useRef(generateViewerId());
  const socketRef = useRef(null);
  const statsIntervalRef = useRef(null);

  // ============= HOOKS =============
  const {
    accessGranted,
    accessCode,
    setAccessCode,
    accessError,
    clientIp,
    accessChecking,
    handleAccessSubmit,
  } = useAccessControl();

  const handleTrackUpdate = useCallback((unitId, kind, track) => {
    setActiveUnits(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(unitId) || { unitId };

      // Create NEW object to ensure React detects the change
      const updated = { ...existing };
      const stream = updated.stream || new MediaStream();

      if (!track) {
        stream.getTracks()
          .filter(t => t.kind === kind)
          .forEach(t => stream.removeTrack(t));
        if (kind === 'video') updated.videoTrack = null;
        if (kind === 'audio') updated.audioTrack = null;
      } else {
        const currentTrack = kind === 'video' ? updated.videoTrack : updated.audioTrack;
        if (currentTrack?.id === track.id) return prev;

        if (track.readyState === 'ended') return prev;

        stream.getTracks()
          .filter(t => t.kind === kind)
          .forEach(t => stream.removeTrack(t));

        stream.addTrack(track);
        if (kind === 'video') updated.videoTrack = track;
        if (kind === 'audio') updated.audioTrack = track;
      }

      updated.stream = stream;
      updated.isOnline = true;
      updated.lastUpdate = Date.now();
      newMap.set(unitId, updated);
      return newMap;
    });
  }, []);

  const mediaSoup = useMediaSoup({
    socket: socketRef,
    onTrackUpdate: handleTrackUpdate,
  });

  // Pass socketRef to useSocket so both hooks share the same ref
  const { socketInstance } = useSocket({
    accessGranted,
    viewerId,
    mediaSoup,
    setConnectionStatus,
    setActiveUnits,
    selectedUnit,
    setSelectedUnit,
    socketRef, // Share the same ref with useMediaSoup
  });

  const {
    isPushToTalkActive,
    isPushToTalkConnecting,
    startPushToTalk,
    stopPushToTalk,
    cleanup: cleanupPTT,
  } = usePushToTalk({
    socket: socketRef, // Use the shared socketRef
    viewerId,
    selectedUnit,
    isDeviceReady: mediaSoup.isDeviceReady,
    device: mediaSoup.device,
    sendTransport: mediaSoup.sendTransport,
  });

  // Recording hook
  const recordings = useRecordings(socketRef, socketInstance);

  // ============= FETCH SETTINGS ONCE ACCESS IS GRANTED =============
  // /api needs the session cookie, so nothing is requested from the access screen
  const { fetchSettings } = recordings;
  useEffect(() => {
    if (accessGranted) fetchSettings();
  }, [accessGranted, fetchSettings]);

  // ============= CLOCK =============
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // ============= DASHBOARD STATS (Today Clips, All Clips, Storage) =============
  // Polls only while access is granted (before that it would only collect 401s)
  useEffect(() => {
    if (!accessGranted) return;

    const fetchStats = async () => {
      try {
        const resp = await fetch(`${SERVER_URL}/api/dashboard-stats`);
        if (resp.ok) {
          const data = await resp.json();
          setDashboardStats(data);
        }
      } catch {
        // Ignore fetch errors
      }
    };

    fetchStats();
    statsIntervalRef.current = setInterval(fetchStats, 30000); // refresh every 30s

    return () => {
      if (statsIntervalRef.current) {
        clearInterval(statsIntervalRef.current);
        statsIntervalRef.current = null;
      }
    };
  }, [accessGranted]);

  // ============= NETWORK QUALITY =============
  useEffect(() => {
    if (connectionStatus === "Connected" || connectionStatus === "Ready") {
      setNetworkQuality('excellent');
    } else if (connectionStatus === "Connecting..." || connectionStatus === "Initializing device...") {
      setNetworkQuality('good');
    } else if (connectionStatus === "Disconnected") {
      setNetworkQuality('poor');
    } else {
      setNetworkQuality('fair');
    }
  }, [connectionStatus]);

  // ============= STALE VIDEO WATCHDOG =============
  useEffect(() => {
    const timer = setInterval(() => {
      const now = Date.now();
      for (const [unitId, unit] of activeUnits.entries()) {
        if (getHlsUrl(unit.metadata)) continue;
        const lastSeen = mediaSoup.getLastTrackAlive(unitId) || 0;
        if (unit.stream && now - lastSeen > STALE_VIDEO_THRESHOLD) {
          console.warn(`Video stale for ${unitId}, requesting keyframe and reconnect`);
          mediaSoup.requestKeyFrame(unitId);
          mediaSoup.attemptReconnect(unitId);
          mediaSoup.setLastTrackAlive(unitId, now);
        }
      }
    }, STALE_CHECK_INTERVAL);
    return () => clearInterval(timer);
  }, [activeUnits, mediaSoup]);

  // ============= AUTO-CLEANUP STALE UNITS =============
  useEffect(() => {
    const timer = setInterval(() => {
      const now = Date.now();
      setActiveUnits(prev => {
        let hasChanges = false;
        const newMap = new Map(prev);

        for (const [unitId, unit] of newMap.entries()) {
          // Skip HLS units
          if (getHlsUrl(unit.metadata)) continue;

          // Remove units with no lastUpdate for UNIT_STALE_TIMEOUT
          const lastUpdate = unit.lastUpdate || 0;
          if (now - lastUpdate > UNIT_STALE_TIMEOUT && !unit.stream) {
            console.log(`🗑️ Removing stale unit ${unitId} (no activity for ${UNIT_STALE_TIMEOUT / 1000}s)`);
            mediaSoup.cleanupUnit(unitId);
            newMap.delete(unitId);
            hasChanges = true;

            // Deselect if this was the selected unit
            if (selectedUnit === unitId) {
              setSelectedUnit(null);
              setViewMode('list');
            }
          }
        }

        return hasChanges ? newMap : prev;
      });
    }, 5000); // Check every 5 seconds

    return () => clearInterval(timer);
  }, [mediaSoup, selectedUnit]);

  // ============= CLEANUP =============
  useEffect(() => {
    return () => {
      cleanupPTT();
    };
  }, [cleanupPTT]);

  // Auto-reset to list view when selected unit disappears or all units gone
  useEffect(() => {
    if (viewMode === 'detail') {
      if ((selectedUnit && !activeUnits.has(selectedUnit)) || activeUnits.size === 0) {
        setSelectedUnit(null);
        setViewMode('list');
      }
    }
  }, [viewMode, selectedUnit, activeUnits]);

  // ============= DERIVED VALUES =============
  // Compute effective view mode synchronously (no useEffect delay)
  const effectiveViewMode = (viewMode === 'detail' && (!selectedUnit || !activeUnits.has(selectedUnit) || activeUnits.size === 0))
    ? 'list' : viewMode;

  const unitArray = useMemo(() => Array.from(activeUnits.entries()), [activeUnits]);
  const onlineCount = useMemo(
    () => unitArray.filter(([_, unit]) => unit.stream || getHlsUrl(unit.metadata)).length,
    [unitArray]
  );

  // ============= HANDLERS =============
  const handleSelectUnit = useCallback((unitId) => {
    setSelectedUnit(unitId);
    setViewMode('detail');
    // Resume/consume audio when selecting unit
    mediaSoup.consumeAudioForUnit(unitId);
  }, [mediaSoup]);

  const handleBackToList = useCallback(() => {
    if (selectedUnit) {
      mediaSoup.pauseAudioForUnit(selectedUnit);
    }
    stopPushToTalk();
    setSelectedUnit(null);
    setViewMode('list');
  }, [selectedUnit, mediaSoup, stopPushToTalk]);

  const handleKeyFrame = useCallback((unitId) => {
    mediaSoup.requestKeyFrame(unitId);
  }, [mediaSoup]);

  const handleAlive = useCallback((unitId) => {
    mediaSoup.setLastTrackAlive(unitId, Date.now());
  }, [mediaSoup]);

  // ============= RECORDING HANDLERS =============
  const handleToggleRecording = useCallback(async (unitId, e) => {
    e.stopPropagation(); // Prevent card selection
    const status = recordings.getRecordingStatus(unitId);
    if (status.isRecording) {
      await recordings.stopRecording(unitId);
    } else {
      await recordings.startRecording(unitId);
    }
  }, [recordings]);

  const handlePlayRecording = useCallback((recording) => {
    setPlayingRecording(recording);
  }, []);

  const handleClosePlayer = useCallback(() => {
    setPlayingRecording(null);
  }, []);

  const handleDownloadRecording = useCallback((recordingId) => {
    window.open(recordings.getDownloadUrl(recordingId), '_blank');
  }, [recordings]);

  // ============= ACCESS SCREEN =============
  if (!accessGranted) {
    return (
      <div className="viewer-app access-screen">
        <div className="access-card">
          <div className="access-logo">EMS</div>
          <div className="access-title">เข้าสู่ระบบดูไลฟ์</div>
          <div className="access-sub">ใส่รหัสเพื่อเข้า ระบบจะจดจำ IP นี้ไว้</div>
          <div className="access-input-wrap">
            <input
              type="password"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              placeholder="Access code"
              className="access-input"
              disabled={accessChecking}
            />
            {accessError && <div className="access-error">{accessError}</div>}
          </div>
          <button className="access-btn" onClick={handleAccessSubmit} disabled={accessChecking}>เข้าใช้งาน</button>
          <div className="access-meta">IP: {clientIp || (accessChecking ? 'กำลังตรวจสอบ...' : 'ไม่ทราบ IP')}</div>
        </div>
        <AccessScreenStyles />
      </div>
    );
  }

  // ============= MAIN RENDER =============
  return (
    <div className="viewer-app">
      {effectiveViewMode !== 'detail' && (
        <>
          <SummaryHeader
            connectionStatus={connectionStatus}
            networkQuality={networkQuality}
            viewerId={viewerId.current}
            currentTime={currentTime}
            onlineCount={onlineCount}
            dashboardStats={dashboardStats}
          />

          {/* Tab Bar */}
          <TabBar activeTab={activeTab} onTabChange={setActiveTab} />
        </>
      )}

      <div className={`viewer-main ${effectiveViewMode === 'detail' ? 'detail-mode' : ''}`}>
        {/* Live Tab */}
        {activeTab === 'live' && (
          <>
            {unitArray.length === 0 ? (
              <EmptyState />
            ) : effectiveViewMode === 'list' ? (
              <CatalogList
                unitArray={unitArray}
                onSelectUnit={handleSelectUnit}
                onKeyFrame={handleKeyFrame}
                onAlive={handleAlive}
                recordingStatus={recordings.recordingStatus}
                onToggleRecording={handleToggleRecording}
                autoRecordEnabled={recordings.settings?.auto_record_all === '1'}
              />
            ) : (
              <DetailView
                selectedUnit={selectedUnit}
                activeUnits={activeUnits}
                unitArray={unitArray}
                networkQuality={networkQuality}
                viewerId={viewerId.current}
                currentTime={currentTime}
                isPushToTalkActive={isPushToTalkActive}
                isPushToTalkConnecting={isPushToTalkConnecting}
                pttAvailable={connectionStatus === 'Ready'}
                onStartPTT={startPushToTalk}
                onStopPTT={stopPushToTalk}
                onBack={handleBackToList}
                onSelectUnit={handleSelectUnit}
                onKeyFrame={handleKeyFrame}
                onAlive={handleAlive}
                recordingStatus={recordings.getRecordingStatus(selectedUnit)}
                onToggleRecording={handleToggleRecording}
              />
            )}
          </>
        )}

        {/* Recordings Tab */}
        {activeTab === 'recordings' && (
          <RecordingsList
            recordings={recordings.recordings}
            pagination={recordings.pagination}
            loading={recordings.loading}
            onFetch={recordings.fetchRecordings}
            onDelete={recordings.deleteRecording}
            onPlay={handlePlayRecording}
            onDownload={handleDownloadRecording}
            units={activeUnits}
          />
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <SettingsPanel
            settings={recordings.settings}
            storageStats={recordings.storageStats}
            onFetchSettings={recordings.fetchSettings}
            onUpdateSettings={recordings.updateSettings}
            onFetchStorageStats={recordings.fetchStorageStats}
            onRunCleanup={recordings.runCleanup}
            onDeleteByDate={recordings.deleteByDate}
          />
        )}
      </div>

      {/* Recording Player Modal */}
      {playingRecording && (
        <RecordingPlayer
          recording={playingRecording}
          streamUrl={recordings.getStreamUrl(playingRecording.id)}
          onClose={handleClosePlayer}
          onDownload={handleDownloadRecording}
        />
      )}

      {/* Hidden Audio Players */}
      {unitArray.map(([unitId, unitData]) => {
        const hlsUrl = getHlsUrl(unitData.metadata);
        if (hlsUrl) return null;

        const shouldPlayAudio = selectedUnit === unitId && viewMode === 'detail';
        const hasAudio = unitData.stream?.getAudioTracks().length > 0;

        if (!hasAudio || !shouldPlayAudio) return null;

        return (
          <AudioPlayer
            key={`audio-${unitId}`}
            stream={unitData.stream}
            unitId={unitId}
            volume={DEFAULT_VOLUME}
          />
        );
      })}

      <MainStyles />
      <GPSMapStyles />
    </div>
  );
}

// ============= SUB-COMPONENTS =============

const TabBar = React.memo(({ activeTab, onTabChange }) => (
  <div className="tab-bar">
    <div className="tab-bar-inner">
      <button className={`tab-btn ${activeTab === 'live' ? 'active' : ''}`} onClick={() => onTabChange('live')}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/></svg>
        Live
      </button>
      <button className={`tab-btn ${activeTab === 'recordings' ? 'active' : ''}`} onClick={() => onTabChange('recordings')}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
        Recordings
      </button>
      <button className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => onTabChange('settings')}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
        Settings
      </button>
    </div>
  </div>
));

const SummaryHeader = React.memo(({ connectionStatus, networkQuality, viewerId, currentTime, onlineCount, dashboardStats }) => (
  <header className="summary-bar">
    <div className="summary-top">
      <div className="brand-block">
        <div className="brand-name">EMS BodyCam Monitor</div>
        <div className={`pill ${connectionStatus === 'Connected' || connectionStatus === 'Ready' ? 'pill-online' : 'pill-offline'}`}>
          {connectionStatus}
        </div>
        <div className="pill pill-accent">{networkQuality.toUpperCase()}</div>
      </div>
      <div className="identity-block">
        <span className="pill pill-muted">ID: {viewerId}</span>
        <span className="pill pill-muted">{currentTime.toLocaleTimeString('en-GB', { hour12: false })}</span>
      </div>
    </div>
    <div className="metric-row">
      <div className="metric-card">
        <div className="metric-label">Online</div>
        <div className="metric-value">{onlineCount}</div>
      </div>
      <div className="metric-card">
        <div className="metric-label">Today Clips</div>
        <div className="metric-value">{dashboardStats.todayClips}</div>
      </div>
      <div className="metric-card">
        <div className="metric-label">All Clips</div>
        <div className="metric-value">{dashboardStats.allClips}</div>
      </div>
      <div className="metric-card">
        <div className="metric-label">Storage</div>
        <div className="metric-value">
          {dashboardStats.diskFreeGB ? dashboardStats.diskFreeGB + ' GB' : '--'}
        </div>
      </div>
    </div>
  </header>
));

const EmptyState = () => (
  <div className="empty-state">
    <div className="empty-icon-wrap">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#bdbdbd" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
    </div>
    <h2>No Units Online</h2>
    <p>Waiting for emergency units to connect...</p>
  </div>
);

const CatalogCard = React.memo(({ unitData, id, onSelect, onKeyFrame, onAlive, isRecording, recordingHasAudio, onToggleRecording, autoRecordEnabled }) => {
  const hlsUrl = getHlsUrl(unitData.metadata);
  const hasStream = unitData.stream && unitData.videoTrack;
  const isLive = hlsUrl ? true : hasStream;
  const name = unitData.metadata?.unitCode || unitData.metadata?.name || id.substring(0, 12);
  const gps = unitData.metadata?.gps;
  const location = (gps?.lat != null && gps?.lng != null) ? `${gps.lat.toFixed(4)}, ${gps.lng.toFixed(4)}` : 'Awaiting GPS';
  const officer = unitData.metadata?.responderName || unitData.metadata?.officerName || unitData.metadata?.officer;
  const updatedAt = new Date(unitData.lastUpdate).toLocaleTimeString('en-GB', { hour12: false });
  const statusText = isLive ? 'LIVE' : 'STANDBY';

  return (
    <button className="item-card" onClick={() => onSelect(id)}>
      <div className="item-media">
        {hlsUrl ? (
          <HlsVideoPlayer src={hlsUrl} unitId={id} muted={true} compact={true} onAlive={onAlive} />
        ) : hasStream ? (
          <VideoPlayer stream={unitData.stream} unitId={id} muted={true} compact={true} onKeyFrame={onKeyFrame} onAlive={onAlive} />
        ) : (
          <div className="media-placeholder">📹</div>
        )}
        {/* Recording indicator */}
        {isRecording && (
          <div className="recording-indicator no-emoji">
            <span className="rec-dot"></span>
            <span className="rec-text">REC</span>
            {recordingHasAudio && <span className="rec-audio-text">AUDIO</span>}
          </div>
        )}
      </div>
      <div className="item-content">
        <div className="item-heading">
          <div className="item-title">
            <span className="unit-badge">EMS</span> {name}
          </div>
          <span className="item-chip">{id.slice(-6)}</span>
        </div>
        <div className="item-tags">
          <span className="tag tag-location">{location}</span>
          {officer && <span className="tag tag-officer">{officer}</span>}
        </div>
        <div className="item-meta">
          <span className="meta-time">{updatedAt}</span>
          <span className={`status-pill ${hasStream ? 'pill-online' : 'pill-offline'} ${hasStream ? 'pill-live-pulse' : ''}`}>{statusText}</span>
        </div>
        {/* Record button - hidden when auto record is enabled */}
        {hasStream && !autoRecordEnabled && (
          <div className="item-actions">
            <button
              className={`record-btn ${isRecording ? 'recording' : ''}`}
              onClick={(e) => onToggleRecording(id, e)}
            >
              {isRecording ? 'Stop Recording' : 'Record'}
            </button>
          </div>
        )}
      </div>
    </button>
  );
}, (prevProps, nextProps) => {
  const prevTrackId = prevProps.unitData.videoTrack?.id;
  const nextTrackId = nextProps.unitData.videoTrack?.id;
  const prevHls = getHlsUrl(prevProps.unitData.metadata);
  const nextHls = getHlsUrl(nextProps.unitData.metadata);

  return (
    prevTrackId === nextTrackId &&
    prevHls === nextHls &&
    prevProps.id === nextProps.id &&
    prevProps.isRecording === nextProps.isRecording &&
    prevProps.recordingHasAudio === nextProps.recordingHasAudio &&
    prevProps.autoRecordEnabled === nextProps.autoRecordEnabled &&
    prevProps.unitData.metadata?.gps?.lat === nextProps.unitData.metadata?.gps?.lat &&
    prevProps.unitData.metadata?.gps?.lng === nextProps.unitData.metadata?.gps?.lng &&
    prevProps.unitData.lastUpdate === nextProps.unitData.lastUpdate
  );
});

const CatalogList = React.memo(({ unitArray, onSelectUnit, onKeyFrame, onAlive, recordingStatus, onToggleRecording, autoRecordEnabled }) => (
  <section className="catalog-section">
    <div className="card-stack">
      {unitArray.map(([id, unitData]) => {
        const recStatus = recordingStatus?.get(id);
        return (
          <CatalogCard
            key={id}
            unitData={unitData}
            id={id}
            onSelect={onSelectUnit}
            onKeyFrame={onKeyFrame}
            onAlive={onAlive}
            isRecording={recStatus?.isRecording || false}
            recordingHasAudio={recStatus?.hasAudio || false}
            onToggleRecording={onToggleRecording}
            autoRecordEnabled={autoRecordEnabled}
          />
        );
      })}
    </div>
  </section>
));

const svgProps = {
  width: 28, height: 28, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor',
  strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round',
};
const MicIcon = () => (
  <svg {...svgProps}>
    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" x2="12" y1="19" y2="22" />
  </svg>
);
const MicOffIcon = () => (
  <svg {...svgProps}>
    <line x1="2" x2="22" y1="2" y2="22" />
    <path d="M18.89 13.23A7.12 7.12 0 0 0 19 12v-2" />
    <path d="M5 10v2a7 7 0 0 0 12 5" />
    <path d="M15 9.34V5a3 3 0 0 0-5.68-1.33" />
    <path d="M9 9v3a3 3 0 0 0 5.12 2.12" />
    <line x1="12" x2="12" y1="19" y2="22" />
  </svg>
);
const SpinnerIcon = () => (
  <svg {...svgProps} strokeWidth={2.5}>
    <circle cx="12" cy="12" r="9" strokeOpacity="0.3" />
    <path d="M21 12a9 9 0 0 0-9-9" />
  </svg>
);

const DetailView = React.memo(({
  selectedUnit,
  activeUnits,
  unitArray,
  networkQuality,
  viewerId,
  currentTime,
  isPushToTalkActive,
  isPushToTalkConnecting,
  pttAvailable,
  onStartPTT,
  onStopPTT,
  onBack,
  onSelectUnit,
  onKeyFrame,
  onAlive,
}) => {
  const selectedUnitData = selectedUnit ? activeUnits.get(selectedUnit) : null;
  const hlsUrl = getHlsUrl(selectedUnitData?.metadata);
  const unitIds = unitArray.map(([id]) => id);
  const currentIndex = unitIds.indexOf(selectedUnit);
  const previousUnitId = currentIndex > 0 ? unitIds[currentIndex - 1] : null;
  const nextUnitId = currentIndex >= 0 && currentIndex < unitIds.length - 1 ? unitIds[currentIndex + 1] : null;

  if (!selectedUnitData) {
    return null;
  }

  const unitLabel = selectedUnitData.metadata?.unitCode || selectedUnitData.metadata?.name || selectedUnit || 'Selected unit';
  const officerLabel = selectedUnitData.metadata?.responderName || selectedUnitData.metadata?.officerName;

  let ptt;
  if (isPushToTalkActive) {
    ptt = { className: 'ptt-active', icon: <MicIcon />, title: `Speaking to ${unitLabel}`, sub: 'Release to stop' };
  } else if (isPushToTalkConnecting) {
    ptt = { className: 'ptt-connecting', icon: <SpinnerIcon />, title: 'Opening mic…', sub: 'Keep holding' };
  } else if (!pttAvailable) {
    ptt = { className: 'ptt-unavailable', icon: <MicOffIcon />, title: 'Talk unavailable', sub: 'Reconnecting to server…' };
  } else {
    ptt = { className: '', icon: <MicIcon />, title: 'Hold to Talk', sub: `Talk to ${unitLabel}` };
  }
  const handlePttDown = (e) => {
    if (!pttAvailable) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    onStartPTT();
  };

  return (
    <div className="detail-screen">
      <div className="detail-topbar">
        <div className="topbar-left">
          <button className="back-button" onClick={onBack}>Return</button>
          <div className="detail-title" title={selectedUnit}>
            {unitLabel}{officerLabel ? <span className="detail-officer"> · {officerLabel}</span> : null}
          </div>
        </div>
        <div className="topbar-right">
          <span className="pill pill-muted">ID {viewerId}</span>
          <span className="pill pill-accent">{networkQuality.toUpperCase()}</span>
        </div>
      </div>

      <div className="detail-grid">
        <div className="video-panel">
          <div className="panel-header">
            <div>
              <div className="panel-title">Live Feed</div>
              <div className="panel-sub">
                {unitLabel} - {selectedUnitData.metadata?.gps?.lat != null ? `${selectedUnitData.metadata.gps.lat.toFixed(4)}, ${selectedUnitData.metadata.gps.lng.toFixed(4)}` : 'Awaiting GPS'}
              </div>
            </div>
            <div className="panel-actions">
              <div className="nav-buttons">
                <button disabled={!previousUnitId} className="secondary-btn" onClick={() => onSelectUnit(previousUnitId)}>Prev</button>
                <button disabled={!nextUnitId} className="primary-btn" onClick={() => onSelectUnit(nextUnitId)}>Next</button>
              </div>
              <div className={`status-chip ${selectedUnitData.stream ? 'chip-online' : 'chip-offline'}`}>
                {hlsUrl || selectedUnitData.stream ? 'LIVE' : 'OFFLINE'}
              </div>
            </div>
          </div>
          <div className="media-shell">
            {hlsUrl ? (
              <HlsVideoPlayer src={hlsUrl} unitId={selectedUnit} muted={false} compact={false} onAlive={onAlive} />
            ) : selectedUnitData.stream ? (
              <VideoPlayer stream={selectedUnitData.stream} unitId={selectedUnit} muted={true} compact={false} onKeyFrame={onKeyFrame} onAlive={onAlive} />
            ) : (
              <div className="media-placeholder large">No video stream</div>
            )}
          </div>
          {/* Pointer events cover mouse, touch and pen: one press never starts twice */}
          <button
            type="button"
            className={`ptt-button ${ptt.className}`}
            aria-disabled={!pttAvailable}
            aria-pressed={isPushToTalkActive}
            onPointerDown={handlePttDown}
            onPointerUp={onStopPTT}
            onPointerLeave={onStopPTT}
            onPointerCancel={onStopPTT}
            onContextMenu={(e) => e.preventDefault()}
          >
            <span className="ptt-icon" aria-hidden="true">{ptt.icon}</span>
            <span className="ptt-text">
              <span className="ptt-title">{ptt.title}</span>
              <span className="ptt-sub">{ptt.sub}</span>
            </span>
            {isPushToTalkActive ? (
              <span className="ptt-meter" aria-hidden="true"><span /><span /><span /><span /><span /></span>
            ) : pttAvailable && !isPushToTalkConnecting ? (
              <span className="ptt-kbd">Hold Space</span>
            ) : null}
          </button>
        </div>

        <div className="info-panel">
          <MetadataPanel unitData={selectedUnitData} compact={false} currentTime={currentTime} />
          <GPSMap gpsData={selectedUnitData.metadata?.gps} unitName={unitLabel} />
        </div>
      </div>
    </div>
  );
});

// ============= STYLES =============

const AccessScreenStyles = () => (
  <style>{`
    .access-screen {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background: radial-gradient(circle at 20% 20%, #eef2ff 0, #f6f7fb 35%, #ffffff 70%);
      padding: 24px 16px;
      box-sizing: border-box;
    }
    .access-card {
      width: 100%;
      max-width: 380px;
      background: #ffffff;
      border-radius: 16px;
      border: 1px solid #e7e9f0;
      box-shadow: 0 20px 60px rgba(31,41,55,0.08);
      padding: 24px 22px;
      text-align: center;
      box-sizing: border-box;
    }
    .access-logo {
      display: inline-flex;
      padding: 10px 14px;
      background: #111827;
      color: #fff;
      font-weight: 800;
      letter-spacing: 0.5px;
      border-radius: 12px;
      margin-bottom: 14px;
    }
    .access-title {
      font-size: 20px;
      font-weight: 700;
      color: #0f172a;
      margin-bottom: 6px;
    }
    .access-sub {
      color: #6b7280;
      font-size: 14px;
      margin-bottom: 18px;
    }
    .access-input-wrap {
      margin-bottom: 12px;
      text-align: left;
    }
    .access-input {
      width: 100%;
      padding: 12px 14px;
      border: 1px solid #d7dce5;
      border-radius: 10px;
      font-size: 16px;
      min-height: 48px;
      box-sizing: border-box;
      outline: none;
      transition: border 0.2s, box-shadow 0.2s;
    }
    .access-input:focus {
      border-color: #2563eb;
      box-shadow: 0 0 0 3px rgba(37,99,235,0.15);
    }
    .access-error {
      color: #ef4444;
      font-size: 13px;
      margin-top: 6px;
    }
    .access-btn {
      width: 100%;
      min-height: 48px;
      padding: 12px;
      border: none;
      border-radius: 10px;
      background: linear-gradient(135deg, #2563eb, #1d4ed8);
      color: #fff;
      font-weight: 700;
      font-size: 15px;
      cursor: pointer;
      box-shadow: 0 12px 30px rgba(37,99,235,0.35);
      transition: transform 0.1s ease, box-shadow 0.2s ease;
    }
    .access-btn:hover {
      transform: translateY(-1px);
      box-shadow: 0 16px 36px rgba(37,99,235,0.45);
    }
    .access-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      box-shadow: none;
    }
    .access-meta {
      margin-top: 12px;
      color: #94a3b8;
      font-size: 12px;
    }
  `}</style>
);

const MainStyles = () => (
  <style>{`
    .viewer-app {
      min-height: 100vh;
      background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 50%, #ffffff 100%);
      color: #1a1a1a;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', sans-serif;
    }
    .summary-bar {
      background: #ffffff;
      border: 1px solid #e8eaed;
      box-shadow: 0 1px 3px rgba(0,0,0,0.05);
      padding: 20px 28px 16px;
      border-radius: 12px;
      margin: 16px;
    }
    .summary-top {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      flex-wrap: wrap;
      align-items: center;
    }
    .brand-block, .identity-block {
      display: flex;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;
    }
    .brand-name {
      font-size: 20px;
      font-weight: 700;
      letter-spacing: -0.3px;
      color: #1a1a1a;
    }
    .pill {
      border-radius: 8px;
      padding: 6px 14px;
      font-weight: 600;
      font-size: 13px;
      border: 1px solid #e8eaed;
      background: #f8f9fa;
      color: #1a1a1a;
    }
    .pill-accent {
      background: #e3f2fd;
      border-color: #90caf9;
      color: #1565c0;
    }
    .pill-muted {
      background: #f5f5f5;
      color: #616161;
      border-color: #e0e0e0;
    }
    .pill-online {
      background: #e8f5e9;
      border-color: #a5d6a7;
      color: #2e7d32;
    }
    .pill-offline {
      background: #fff3e0;
      border-color: #ffcc80;
      color: #ef6c00;
    }
    .metric-row {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 16px;
      margin-top: 18px;
    }
    .metric-card {
      background: #fafafa;
      border: 1px solid #e8eaed;
      border-radius: 10px;
      padding: 18px 20px;
    }
    .metric-label {
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.8px;
      color: #757575;
      margin-bottom: 6px;
      font-weight: 600;
    }
    .metric-value {
      font-size: 26px;
      font-weight: 700;
      color: #1a1a1a;
    }
    .viewer-main {
      padding: 20px;
      max-width: 100%;
      margin: 0 auto;
    }
    .viewer-main.detail-mode {
      padding: 16px;
      max-width: 100%;
    }
    .empty-state {
      background: #ffffff;
      border: 1px solid #e8eaed;
      border-radius: 12px;
      padding: 80px 40px;
      text-align: center;
      margin: 40px auto;
      max-width: 600px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    }
    .empty-state .empty-icon {
      font-size: 72px;
      margin-bottom: 24px;
      opacity: 0.15;
    }
    .empty-state h2 {
      font-size: 22px;
      font-weight: 600;
      margin: 0 0 12px 0;
      color: #1a1a1a;
    }
    .empty-state p {
      color: #616161;
      margin: 0;
      font-size: 15px;
      line-height: 1.6;
    }
    .catalog-section {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
    .card-stack {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 18px;
      padding: 4px;
    }
    .item-card {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      background: #ffffff;
      border: 1px solid #e8eaed;
      border-radius: 12px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.06);
      padding: 14px;
      gap: 12px;
      cursor: pointer;
      transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    }
    .item-card:hover,
    .item-card:focus-visible {
      transform: translateY(-3px);
      box-shadow: 0 6px 16px rgba(0,0,0,0.12);
      border-color: #bdbdbd;
      outline: none;
    }
    .item-media {
      aspect-ratio: 16 / 9;
      background: #1a1a1a;
      border-radius: 10px;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
    }
    .media-placeholder {
      color: #757575;
      font-size: 36px;
    }
    .media-placeholder.large {
      font-size: 28px;
      color: #9e9e9e;
    }
    .item-content {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    .item-heading {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
    }
    .item-title {
      font-weight: 600;
      font-size: 15px;
      color: #1a1a1a;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .item-chip {
      border: 1px solid #e0e0e0;
      padding: 4px 10px;
      border-radius: 6px;
      font-size: 11px;
      color: #616161;
      background: #f5f5f5;
      font-weight: 500;
    }
    .item-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }
    .tag {
      background: #fafafa;
      border: 1px solid #e8eaed;
      border-radius: 6px;
      padding: 6px 10px;
      font-size: 12px;
      color: #424242;
      font-weight: 500;
    }
    .item-meta {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      font-size: 12px;
      color: #757575;
      font-weight: 500;
    }
    .status-pill {
      padding: 6px 12px;
      border-radius: 6px;
      font-weight: 600;
      font-size: 11px;
      border: 1px solid transparent;
    }
    .status-pill.pill-online {
      background: #e8f5e9;
      border-color: #a5d6a7;
      color: #2e7d32;
    }
    .status-pill.pill-offline {
      background: #fff3e0;
      border-color: #ffcc80;
      color: #ef6c00;
    }
    .detail-screen {
      display: flex;
      flex-direction: column;
      gap: 14px;
      min-height: 100vh;
    }
    .detail-topbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 14px;
      background: #ffffff;
      border: 1px solid #e8eaed;
      border-radius: 10px;
      padding: 14px 18px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.06);
      position: sticky;
      top: 0;
      z-index: 10;
      margin: 0 16px;
    }
    .topbar-left {
      display: flex;
      align-items: center;
      gap: 12px;
    }
    .topbar-right {
      display: flex;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;
    }
    .back-button {
      background: #1976d2;
      color: #ffffff;
      border: none;
      border-radius: 8px;
      padding: 10px 16px;
      font-weight: 600;
      cursor: pointer;
      box-shadow: 0 2px 6px rgba(25,118,210,0.3);
      transition: all 0.2s ease;
    }
    .back-button:hover {
      background: #1565c0;
      box-shadow: 0 4px 10px rgba(25,118,210,0.4);
    }
    .detail-title {
      font-weight: 600;
      font-size: 16px;
      letter-spacing: -0.2px;
      color: #1a1a1a;
    }
    .detail-grid {
      display: grid;
      grid-template-columns: 2fr 1fr;
      gap: 20px;
      padding: 16px;
    }
    .video-panel, .info-panel {
      background: #ffffff;
      border: 1px solid #e8eaed;
      border-radius: 12px;
      padding: 18px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.04);
      display: flex;
      flex-direction: column;
      gap: 14px;
    }
    .panel-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 10px;
    }
    .panel-title {
      font-weight: 600;
      font-size: 16px;
      color: #1a1a1a;
    }
    .panel-sub {
      font-size: 13px;
      color: #757575;
      margin-top: 2px;
    }
    .status-chip {
      padding: 6px 14px;
      border-radius: 6px;
      font-weight: 600;
      font-size: 12px;
      border: 1px solid #e8eaed;
    }
    .status-chip.chip-online {
      background: #e8f5e9;
      border-color: #a5d6a7;
      color: #2e7d32;
    }
    .status-chip.chip-offline {
      background: #fff3e0;
      border-color: #ffcc80;
      color: #ef6c00;
    }
    .media-shell {
      border-radius: 10px;
      overflow: hidden;
      background: #1a1a1a;
      width: 100%;
      aspect-ratio: 16 / 9;
    }
    .panel-actions {
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .ptt-button {
      position: relative;
      width: 100%;
      min-height: 84px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 18px;
      padding: 0 132px;
      border: none;
      border-radius: 14px;
      background: #263238;
      color: #ffffff;
      font-family: inherit;
      text-align: left;
      cursor: pointer;
      box-shadow: 0 4px 12px rgba(38,50,56,0.25);
      transition: background 0.15s ease, box-shadow 0.15s ease;
      user-select: none;
      -webkit-user-select: none;
      -webkit-touch-callout: none;
      -webkit-tap-highlight-color: transparent;
      touch-action: none;
    }
    .ptt-button:hover {
      background: #37474f;
    }
    .ptt-button:focus-visible {
      outline: 3px solid #90caf9;
      outline-offset: 2px;
    }
    .ptt-button.ptt-connecting {
      background: #37474f;
    }
    .ptt-button.ptt-active {
      background: #2e7d32;
      box-shadow: 0 6px 18px rgba(46,125,50,0.35);
    }
    .ptt-button.ptt-unavailable {
      background: #eceff1;
      color: #37474f;
      border: 1px dashed #b0bec5;
      box-shadow: none;
      cursor: not-allowed;
    }
    .ptt-icon {
      width: 52px;
      height: 52px;
      border-radius: 50%;
      background: rgba(255,255,255,0.12);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .ptt-active .ptt-icon {
      background: rgba(255,255,255,0.22);
      animation: ptt-pulse 1.1s ease-out infinite;
    }
    .ptt-connecting .ptt-icon svg {
      animation: ptt-spin 0.9s linear infinite;
    }
    .ptt-unavailable .ptt-icon {
      background: #dde3e7;
    }
    .ptt-text {
      display: flex;
      flex-direction: column;
      gap: 4px;
      min-width: 0;
    }
    .ptt-title {
      font-size: 20px;
      font-weight: 700;
      letter-spacing: -0.2px;
    }
    .ptt-sub {
      font-size: 14px;
      color: #b0bec5;
    }
    .ptt-connecting .ptt-sub {
      color: #cfd8dc;
    }
    .ptt-active .ptt-sub {
      color: #e8f5e9;
    }
    .ptt-unavailable .ptt-sub {
      color: #455a64;
    }
    .ptt-kbd, .ptt-meter {
      position: absolute;
      right: 24px;
      top: 50%;
      transform: translateY(-50%);
    }
    .ptt-kbd {
      font-size: 13px;
      font-weight: 600;
      padding: 7px 12px;
      border-radius: 6px;
      border: 1px solid rgba(255,255,255,0.3);
      background: rgba(255,255,255,0.08);
      color: #eceff1;
    }
    .ptt-meter {
      display: flex;
      align-items: center;
      gap: 4px;
      height: 28px;
    }
    .ptt-meter span {
      width: 4px;
      border-radius: 2px;
      background: #ffffff;
    }
    .ptt-meter span:nth-child(1) { height: 10px; }
    .ptt-meter span:nth-child(2) { height: 20px; }
    .ptt-meter span:nth-child(3) { height: 28px; }
    .ptt-meter span:nth-child(4) { height: 16px; }
    .ptt-meter span:nth-child(5) { height: 8px; }
    @keyframes ptt-pulse {
      0% { box-shadow: 0 0 0 0 rgba(255,255,255,0.55); }
      100% { box-shadow: 0 0 0 14px rgba(255,255,255,0); }
    }
    @keyframes ptt-spin {
      to { transform: rotate(360deg); }
    }
    @media (prefers-reduced-motion: reduce) {
      .ptt-active .ptt-icon,
      .ptt-connecting .ptt-icon svg {
        animation: none;
      }
    }
    .nav-buttons {
      display: flex;
      gap: 10px;
    }
    .primary-btn, .secondary-btn {
      padding: 10px 16px;
      border-radius: 8px;
      font-weight: 600;
      border: 1px solid #e8eaed;
      background: #f5f5f5;
      cursor: pointer;
      transition: all 0.2s ease;
      font-size: 13px;
    }
    .secondary-btn:hover:not(:disabled) {
      background: #e0e0e0;
    }
    .primary-btn {
      background: #1976d2;
      border-color: #1976d2;
      color: #ffffff;
      box-shadow: 0 2px 6px rgba(25,118,210,0.3);
    }
    .primary-btn:hover:not(:disabled) {
      background: #1565c0;
      box-shadow: 0 4px 10px rgba(25,118,210,0.4);
    }
    .primary-btn:disabled, .secondary-btn:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
    .info-panel {
      min-width: 0;
    }
    /* Breakpoints: desktop 4 cols (>=1440), laptop 3 (1024-1439), tablet 2 (640-1023), phone 1 (<640) */
    @media (max-width: 1439px) {
      .card-stack {
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 16px;
      }
    }
    @media (max-width: 1100px) {
      .detail-grid {
        grid-template-columns: minmax(0, 1fr);
      }
    }
    @media (max-width: 1023px) {
      .card-stack {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 14px;
      }
      .item-card {
        padding: 12px;
      }
    }
    @media (min-width: 640px) and (max-width: 1100px) {
      .info-panel {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        align-items: stretch;
      }
    }
    @media (max-width: 768px) {
      .metric-row {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 12px;
      }
      .metric-value {
        font-size: 22px;
      }
      .summary-bar {
        margin: 12px;
      }
      .detail-grid {
        padding: 12px;
        gap: 14px;
      }
      .detail-topbar {
        margin: 0 12px;
      }
      .viewer-main {
        padding: 12px;
      }
    }
    @media (max-width: 639px) {
      .card-stack {
        grid-template-columns: minmax(0, 1fr);
        gap: 14px;
        padding: 0;
      }
      .summary-bar {
        margin: 8px;
        padding: 14px;
      }
      .summary-top {
        gap: 8px;
      }
      .brand-block, .identity-block {
        gap: 6px;
      }
      .brand-name {
        font-size: 17px;
        width: 100%;
      }
      .pill {
        padding: 4px 10px;
        font-size: 12px;
      }
      .metric-row {
        gap: 8px;
        margin-top: 12px;
      }
      .metric-card {
        padding: 10px 12px;
      }
      .metric-label {
        font-size: 10px;
        margin-bottom: 2px;
      }
      .metric-value {
        font-size: 20px;
      }
      .viewer-main {
        padding: 8px;
      }
      .viewer-main.detail-mode {
        padding: 8px 0;
      }
      .item-card {
        padding: 12px;
        border-radius: 10px;
      }
      .item-title {
        font-size: 15px;
      }
      .detail-screen {
        gap: 10px;
      }
      .detail-topbar {
        margin: 0 8px;
        padding: 8px 10px;
        gap: 8px;
      }
      .topbar-right .pill-muted {
        display: none;
      }
      .detail-title {
        font-size: 15px;
      }
      .detail-grid {
        padding: 8px;
        gap: 10px;
      }
      .video-panel, .info-panel {
        padding: 12px;
        gap: 10px;
      }
      .ptt-button {
        min-height: 92px;
        padding: 0 20px;
        border-radius: 18px;
      }
      .ptt-meter {
        position: static;
        transform: none;
      }
      .panel-header {
        flex-wrap: wrap;
      }
      .panel-actions {
        flex: 1 1 100%;
      }
      .nav-buttons {
        flex: 1;
      }
      .nav-buttons > button {
        flex: 1;
      }
      .empty-state {
        padding: 48px 20px;
        margin: 16px auto;
      }
    }
    @keyframes pulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.7; transform: scale(1.05); }
    }
    @keyframes spin {
      from { transform: translate(-50%, -50%) rotate(0deg); }
      to { transform: translate(-50%, -50%) rotate(360deg); }
    }
    @keyframes blink {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.3; }
    }

    /* Tab Bar */
    .tab-bar {
      padding: 0 16px 16px;
      margin: 0 16px;
    }
    .tab-bar-inner {
      display: flex;
      gap: 4px;
      background: #f5f5f5;
      border: 1px solid #e8eaed;
      border-radius: 10px;
      padding: 4px;
    }
    .tab-btn {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 10px 20px;
      border: none;
      border-radius: 8px;
      background: transparent;
      font-weight: 600;
      font-size: 14px;
      cursor: pointer;
      transition: all 0.2s;
      color: #616161;
    }
    .tab-btn:hover {
      background: #eee;
      color: #424242;
    }
    .tab-btn.active {
      background: #1976d2;
      color: #fff;
      box-shadow: 0 2px 8px rgba(25,118,210,0.25);
    }
    .tab-btn svg {
      flex-shrink: 0;
    }

    /* Unit Badge & Tags */
    .unit-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: #1976d2;
      color: #fff;
      font-size: 9px;
      font-weight: 800;
      padding: 2px 6px;
      border-radius: 4px;
      letter-spacing: 0.5px;
      margin-right: 4px;
      vertical-align: middle;
    }
    .tag-location::before {
      content: '';
      display: inline-block;
      width: 6px;
      height: 6px;
      background: #1976d2;
      border-radius: 50%;
      margin-right: 6px;
      vertical-align: middle;
    }
    .tag-officer::before {
      content: '';
      display: inline-block;
      width: 6px;
      height: 6px;
      background: #7b1fa2;
      border-radius: 50%;
      margin-right: 6px;
      vertical-align: middle;
    }
    .meta-time {
      font-variant-numeric: tabular-nums;
    }
    .pill-live-pulse {
      animation: livePulse 2s ease-in-out infinite;
    }
    @keyframes livePulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(46,125,50,0.3); }
      50% { box-shadow: 0 0 0 6px rgba(46,125,50,0); }
    }
    .empty-icon-wrap {
      width: 80px;
      height: 80px;
      background: #f5f5f5;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 20px;
    }

    /* Recording Indicator */
    .recording-indicator {
      position: absolute;
      top: 10px;
      right: 10px;
      background: rgba(211, 47, 47, 0.85);
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
      color: #fff;
      padding: 5px 12px;
      border-radius: 6px;
      font-size: 11px;
      font-weight: 700;
      display: flex;
      align-items: center;
      gap: 6px;
      box-shadow: 0 2px 8px rgba(211,47,47,0.4);
    }
    .rec-dot {
      width: 8px;
      height: 8px;
      background: #fff;
      border-radius: 50%;
      animation: blink 1s infinite;
    }
    .rec-text {
      letter-spacing: 0.5px;
    }
    .rec-audio-text {
      font-size: 10px;
      background: rgba(255,255,255,0.2);
      padding: 2px 4px;
      border-radius: 4px;
      letter-spacing: 0.5px;
    }

    /* Record Button */
    .item-actions {
      margin-top: 10px;
      display: flex;
      gap: 8px;
    }
    .record-btn {
      padding: 8px 16px;
      border: 1px solid #d32f2f;
      border-radius: 6px;
      background: #fff;
      color: #d32f2f;
      font-weight: 600;
      font-size: 12px;
      cursor: pointer;
      transition: all 0.2s;
    }
    .record-btn:hover {
      background: #d32f2f;
      color: #fff;
    }
    .record-btn.recording {
      background: #d32f2f;
      color: #fff;
      animation: pulse 1.5s infinite;
    }

    /* Item media relative positioning */
    .item-media {
      position: relative;
    }

    /* Responsive guards */
    .viewer-app,
    .viewer-app *,
    .viewer-app *::before,
    .viewer-app *::after {
      box-sizing: border-box;
    }
    .viewer-app {
      overflow-x: clip;
      overflow-wrap: break-word;
    }
    .item-card {
      min-width: 0;
      width: 100%;
      font: inherit;
      color: inherit;
      text-align: left;
    }
    .item-heading, .item-title, .item-content {
      min-width: 0;
    }
    .detail-grid > *, .video-panel, .info-panel, .panel-header > div:first-child {
      min-width: 0;
    }
    .topbar-left {
      min-width: 0;
      flex: 1 1 auto;
    }
    .topbar-right {
      flex: 0 0 auto;
    }
    .detail-title {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .detail-officer {
      color: #757575;
      font-weight: 500;
    }
    .panel-sub {
      overflow-wrap: anywhere;
    }

    /* Tab bar overrides live here because the base tab rules are declared after the breakpoints */
    @media (max-width: 768px) {
      .tab-bar {
        margin: 0 12px;
        padding: 0 0 12px;
      }
    }
    @media (max-width: 639px) {
      .tab-bar {
        margin: 0 8px;
      }
      .tab-btn {
        flex-direction: column;
        gap: 2px;
        padding: 6px 4px;
        font-size: 12px;
        min-width: 0;
      }
    }

    /* Touch targets >= 44px on touch screens and small viewports */
    @media (max-width: 1024px), (pointer: coarse) {
      .tab-btn,
      .back-button,
      .record-btn,
      .primary-btn,
      .secondary-btn {
        min-height: 44px;
      }
      .record-btn {
        padding: 10px 18px;
        font-size: 13px;
      }
      .nav-buttons > button {
        min-width: 64px;
      }
    }
    @media (hover: none) {
      .ptt-kbd {
        display: none;
      }
    }
  `}</style>
);
